/**
 * Session du joueur — store Zustand **en mémoire uniquement** (DECISIONS §4.1 et §8).
 *
 * Points non négociables :
 * - `accessToken` n’est **jamais** écrit dans `localStorage`, `sessionStorage`, un cookie ou un
 *   indexedDB. Un rechargement de page repart d’un `/auth/refresh` (cookie `HttpOnly`), ce qui
 *   rend le jeton inaccessible au JavaScript d’une éventuelle injection.
 * - `eclats` et `niveauMax` proviennent du serveur et sont seulement **affichés** : aucun calcul
 *   local, aucune correction optimiste (DECISIONS §8, `docs/api-v1.md` §11.1).
 */
import { create } from 'zustand';

import type { Progression, User } from '@elemora/shared/api';

/** Où en est la connaissance de la session au démarrage de l’application. */
export type StatutSession =
  /** Rien n’est encore décidé : une tentative de restauration est en cours. */
  | 'inconnu'
  /** Aucune session valide : les routes protégées redirigent vers `/connexion`. */
  | 'anonyme'
  /** Session établie (utilisateur et progression connus du serveur). */
  | 'connecte';

export interface EtatSession {
  statut: StatutSession;
  user: User | null;
  progression: Progression | null;
  accessToken: string | null;
  definirSession: (session: { user: User; progression: Progression; accessToken: string }) => void;
  definirAccessToken: (accessToken: string) => void;
  definirUtilisateur: (user: User) => void;
  definirProgression: (progression: Progression) => void;
  deconnecter: () => void;
}

/** Store unique de session. Aucun middleware `persist` : c’est volontaire et documenté. */
export const useSessionStore = create<EtatSession>((set) => ({
  statut: 'inconnu',
  user: null,
  progression: null,
  accessToken: null,
  definirSession: ({ user, progression, accessToken }) =>
    set({ statut: 'connecte', user, progression, accessToken }),
  definirAccessToken: (accessToken) => set({ accessToken }),
  definirUtilisateur: (user) => set({ user }),
  definirProgression: (progression) => set({ progression }),
  deconnecter: () =>
    set({ statut: 'anonyme', user: null, progression: null, accessToken: null }),
}));

/** Jeton courant, lu hors composant React (client HTTP). */
export function lireAccessToken(): string | null {
  return useSessionStore.getState().accessToken;
}
